import { useState, useRef } from "react";
import api, { formatApiErrorDetail, API } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Copy, Check, UploadCloud, Loader2, Smartphone } from "lucide-react";
import { toast } from "sonner";

export function UpiPaymentModal({ open, onClose, course, upiConfig, onSubmitted }) {
  const [copied, setCopied] = useState(false);
  const [utr, setUtr] = useState("");
  const [screenshotPath, setScreenshotPath] = useState("");
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();

  const upiId = upiConfig?.upi_id || "";
  const payee = upiConfig?.payee_name || "EZ Education";
  const qrSrc = upiConfig?.qr_path ? `${API}/upi-qr` : `${process.env.PUBLIC_URL || ""}/upi-qr.png`;

  const copyUpi = () => {
    navigator.clipboard.writeText(upiId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setScreenshotPath(data.path);
      toast.success("Screenshot uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!utr.trim() || !screenshotPath) {
      toast.error("Enter UTR/transaction ID and upload the payment screenshot");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/payments", { course_id: course.id, utr: utr.trim(), screenshot_path: screenshotPath });
      toast.success("Payment submitted! Awaiting admin approval.");
      onSubmitted?.();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="upi-modal" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <Smartphone className="h-5 w-5 text-teal-700" /> Pay ₹{course.price} via UPI
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="rounded-xl border border-slate-200 p-4 bg-slate-50">
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-700 mb-3">Step 1 · Pay</p>
            <div className="flex gap-4 items-center">
              <img data-testid="upi-qr-image" src={qrSrc} alt="UPI QR" className="h-40 w-40 rounded-lg bg-white border border-slate-200 object-contain p-1 shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-slate-500 mb-1">Scan QR or pay to UPI ID:</p>
                <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
                  <span className="font-mono text-sm text-slate-800 truncate">{upiId}</span>
                  <button data-testid="upi-id-copy-button" onClick={copyUpi} className="ml-auto text-teal-700 hover:text-teal-600">
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-2">Payee: {payee}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-700 mb-3">Step 2 · Submit proof</p>
            <div className="space-y-3">
              <div>
                <Label htmlFor="utr">Transaction ID / UTR Number</Label>
                <Input id="utr" data-testid="payment-utr-input" value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="12-digit UTR" className="mt-1.5" />
              </div>
              <div>
                <Label>Payment Screenshot</Label>
                <input ref={fileRef} data-testid="payment-screenshot-upload" type="file" accept="image/*" onChange={handleFile} className="hidden" />
                <button
                  onClick={() => fileRef.current?.click()}
                  className="mt-1.5 w-full border-2 border-dashed border-slate-300 rounded-xl p-4 flex flex-col items-center gap-2 hover:border-teal-400 transition-colors duration-200"
                >
                  {preview ? (
                    <img src={preview} alt="preview" className="h-24 rounded-lg object-contain" />
                  ) : (
                    <>
                      <UploadCloud className="h-6 w-6 text-slate-400" />
                      <span className="text-sm text-slate-500">Click to upload screenshot</span>
                    </>
                  )}
                  {uploading && <span className="text-xs text-teal-700 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Uploading...</span>}
                </button>
              </div>
            </div>
          </div>

          <Button data-testid="payment-submit-button" onClick={submit} disabled={submitting || uploading} className="w-full bg-teal-700 hover:bg-teal-600 text-white h-11">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit for Approval"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
